import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '../../queries/contributors/use-get-event-contributors.ts';
import type { CreateContributorDto } from '@/types/dtos/contributors/request/create-contributor.dto.ts';
import { GET_PERMISSIONS_QUERY_KEY } from '../../queries/permissions/use-permissions.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';

export const useCreateContributorMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (createContributorData: CreateContributorDto) => {
      const token = await getToken();
      return createContributor(createContributorData, token ?? '');
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_PERMISSIONS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_SESSIONS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_FLOWS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_FLOW_QUERY_KEY],
        }),
      ]);
    },
  });
};

const createContributor = async (createContributorData: CreateContributorDto, token: string) => {
  const url = `${import.meta.env.VITE_API_URL}/contributors/event/${createContributorData.event}`;
  return sendJSONRequest({ url, method: METHOD.POST, body: createContributorData, token });
};

export default useCreateContributorMutation;
